import { useEffect, useState } from "react";
import CyberpunkTunnel from "@/components/feature/CyberpunkTunnel";

const GLYPHS = "アイウエオカキクケコサシスセソタチツテト0123456789ABCDEF#$%&*";

function GlitchText({ text }: { text: string }) {
  const [display, setDisplay] = useState(text);

  useEffect(() => {
    let frame = 0;
    const id = window.setInterval(() => {
      frame++;
      if (frame % 9 === 0) {
        const i = Math.floor(Math.random() * text.length);
        const ch = GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
        const arr = text.split("");
        if (text[i] !== " ") arr[i] = ch;
        setDisplay(arr.join(""));
        window.setTimeout(() => setDisplay(text), 80);
      }
    }, 90);
    return () => window.clearInterval(id);
  }, [text]);

  return <span>{display}</span>;
}

export default function HeroSection() {
  return (
    <section className="relative h-screen min-h-[720px] w-full overflow-hidden bg-[#01010a] text-white">
      <CyberpunkTunnel
        params={{
          lightIntensity: 1.4,
          bloomStrength: 1.5,
          bloomRadius: 0.5,
          bloomThreshold: 0.1,
          reflectionStrength: 0.45,
          ghostIntensity: 1.2,
          matrixIntensity: 0.3,
        }}
      />

      {/* dark vignette overlay for legibility */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_transparent_30%,_rgba(0,0,0,0.55)_75%,_rgba(0,0,0,0.85)_100%)]" />
      {/* scanlines */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay"
        style={{
          backgroundImage:
            "repeating-linear-gradient(to bottom, rgba(255,255,255,0.08) 0px, rgba(255,255,255,0.08) 1px, transparent 1px, transparent 3px)",
        }}
      />

      {/* top nav */}
      <header className="absolute left-0 right-0 top-0 z-20 flex items-center justify-between px-6 py-5 md:px-12 md:py-6">
        <div className="flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-md border border-[#ff2e88]/60 bg-black/30 backdrop-blur-sm">
            <i className="ri-flashlight-fill text-[#ff2e88]" />
          </div>
          <span
            className="text-lg tracking-[0.35em] text-white/90"
            style={{ fontFamily: "'Orbitron', sans-serif" }}
          >
            NEO//RUN
          </span>
        </div>
        <nav className="hidden items-center gap-9 text-xs tracking-[0.3em] text-white/70 md:flex">
          <a href="#world" className="transition hover:text-white">
            WORLD
          </a>
          <a href="#agents" className="transition hover:text-white">
            AGENTS
          </a>
          <a href="#lore" className="transition hover:text-white">
            LORE
          </a>
          <a href="#downloads" className="transition hover:text-white">
            DOWNLOADS
          </a>
        </nav>
        <button
          type="button"
          className="group relative flex items-center gap-2 whitespace-nowrap rounded-md border border-cyan-400/50 bg-black/30 px-4 py-2 text-xs tracking-[0.25em] text-cyan-300 backdrop-blur-sm transition hover:border-cyan-300 hover:text-white cursor-pointer"
        >
          <i className="ri-shield-keyhole-line" />
          JACK IN
        </button>
      </header>

      {/* corner brackets */}
      <div className="pointer-events-none absolute left-6 top-24 z-10 hidden md:block">
        <div className="text-[10px] tracking-[0.35em] text-cyan-300/70">
          /// SECTOR 7 — 2087.06.17
        </div>
        <div className="mt-2 h-12 w-px bg-gradient-to-b from-cyan-300/80 to-transparent" />
      </div>
      <div className="pointer-events-none absolute right-6 top-24 z-10 hidden text-right md:block">
        <div className="text-[10px] tracking-[0.35em] text-[#ff2e88]/80">
          SIGNAL // 0xFA-93B7
        </div>
        <div className="mt-2 ml-auto h-12 w-px bg-gradient-to-b from-[#ff2e88]/80 to-transparent" />
      </div>

      {/* main content */}
      <div className="relative z-10 flex h-full w-full flex-col items-center justify-center px-6 text-center" style={{ transform: "translateY(110px)" }}>
        {/* small pill badge above title */}
        <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] px-4 py-1.5 text-xs text-white/70 backdrop-blur-sm">
          <span className="h-1.5 w-1.5 rounded-full bg-[#ff2e88] shadow-[0_0_8px_rgba(255,46,136,0.9)]" />
          <span className="tracking-wide">
            Sector 7 access opens <span className="text-white">06.17.2087</span>
          </span>
        </div>

        <h1
          className="max-w-5xl text-5xl leading-[1.05] tracking-tight text-white md:text-7xl lg:text-[88px]"
          style={{
            fontFamily: "'Instrument Serif', serif",
            fontWeight: 400,
          }}
        >
          <span className="block">
            <GlitchText text="Enter the Pulse of" />
          </span>
          <span className="block italic">
            <GlitchText text="the Neon Corridor" />
          </span>
        </h1>

        <p className="mt-8 max-w-2xl text-sm leading-relaxed text-white/55 md:text-base">
          A high-velocity descent through the under-grid of Sector 7. Hack the
          rings, ride the data streams, outrun the reflections — your run
          starts when the lights stop flickering.
        </p>

        <div className="mt-10">
          <button
            type="button"
            className="flex items-center gap-2 whitespace-nowrap rounded-full bg-white px-8 py-3.5 text-sm font-medium text-black transition hover:bg-white/90 cursor-pointer"
          >
            Start the Run
          </button>
        </div>
      </div>

      {/* scroll cue */}
      <div className="pointer-events-none absolute bottom-6 left-1/2 z-10 -translate-x-1/2 text-center">
        <div className="text-[10px] tracking-[0.5em] text-white/50">SCROLL</div>
        <div className="mx-auto mt-2 h-8 w-px animate-pulse bg-gradient-to-b from-white/80 to-transparent" />
      </div>
    </section>
  );
}